import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Package, Eye, Download, Edit, Mail, XCircle, CheckSquare, Square, Ban, DollarSign, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import AdminSearchFilter from './AdminSearchFilter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersManagement = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [overrideData, setOverrideData] = useState({ status: '', reason: '' });
  const [shippingLabel, setShippingLabel] = useState('');
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [bulkCancelling, setBulkCancelling] = useState(false);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const [resendingEmailId, setResendingEmailId] = useState(null);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  // Filter orders
  const filteredOrders = useMemo(() => {
    let result = [...orders];
    
    // Search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(o => 
        o.id?.toLowerCase().includes(query) ||
        o.user_email?.toLowerCase().includes(query) ||
        o.guest_email?.toLowerCase().includes(query) ||
        o.shipping_address?.full_name?.toLowerCase().includes(query)
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(o => o.status === statusFilter);
    }
    
    // Payment status filter
    if (paymentFilter !== 'all') {
      result = result.filter(o => o.payment_status === paymentFilter);
    }
    
    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      result = result.filter(o => {
        if (!o.created_at) return false;
        const orderDate = new Date(o.created_at);
        const daysDiff = (now - orderDate) / (1000 * 60 * 60 * 24);
        switch (dateFilter) {
          case 'today': return daysDiff < 1;
          case 'week': return daysDiff < 7;
          case 'month': return daysDiff < 30;
          default: return true;
        }
      });
    }
    
    return result;
  }, [orders, searchQuery, statusFilter, paymentFilter, dateFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPaymentFilter('all');
    setDateFilter('all');
  };

  const handleStatusOverride = async () => {
    if (!overrideData.status) {
      toast.error('Please select a status');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/orders/${selectedOrder.id}/override-status`,
        overrideData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.email_sent) {
        toast.success('Order status updated! Customer has been notified via email.');
      } else {
        toast.success('Order status updated successfully!');
      }
      
      setShowOverrideModal(false);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const handleGenerateLabel = async (order) => {
    try {
      const response = await axios.post(
        `${API}/admin/orders/${order.id}/generate-label`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShippingLabel(response.data.label_html);
      setShowLabelModal(true);
    } catch (error) {
      toast.error('Failed to generate shipping label');
    }
  };

  const printLabel = () => {
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(shippingLabel);
    printWindow.document.close();
    printWindow.print();
  };

  const toggleOrderSelection = (orderId) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const selectAllCancellable = () => {
    const cancellable = orders.filter(o => canCancelOrder(o)).map(o => o.id);
    if (selectedOrders.size === cancellable.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(cancellable));
    }
  };

  const canCancelOrder = (order) => {
    const status = order.maksura_tracking?.status || 'pending';
    return !['in_transit', 'delivered', 'cancelled'].includes(status) && 
           order.payment_status !== 'cancelled';
  };

  const handleBulkCancel = async () => {
    if (selectedOrders.size === 0) {
      toast.error('No orders selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to cancel ${selectedOrders.size} order(s)?`)) {
      return;
    }

    setBulkCancelling(true);
    try {
      const response = await axios.post(
        `${API}/admin/orders/bulk-cancel`,
        { order_ids: Array.from(selectedOrders) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Successfully cancelled ${response.data.cancelled_count} order(s)`);
      setSelectedOrders(new Set());
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel orders');
    } finally {
      setBulkCancelling(false);
    }
  };

  const handleSingleCancel = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    setCancellingOrderId(orderId);
    try {
      await axios.post(
        `${API}/admin/orders/${orderId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order cancelled successfully');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setCancellingOrderId(null);
    }
  };

  const handleMarkPaid = async (orderId) => {
    if (!window.confirm('Mark this order as PAID and send confirmation email to customer?')) {
      return;
    }

    setMarkingPaidId(orderId);
    try {
      const response = await axios.post(
        `${API}/admin/orders/${orderId}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.email_sent) {
        toast.success(`Order marked as paid. Confirmation email sent to ${response.data.recipient}`);
      } else {
        toast.success('Order marked as paid (email not sent - check recipient)');
      }
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark order as paid');
    } finally {
      setMarkingPaidId(null);
    }
  };

  const handleResendEmail = async (orderId) => {
    setResendingEmailId(orderId);
    try {
      const response = await axios.post(
        `${API}/admin/orders/${orderId}/resend-confirmation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(`Confirmation email sent to ${response.data.recipient}`);
      } else {
        toast.error(response.data.error || 'Failed to send email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResendingEmailId(null);
    }
  };

  const handleApprovePaysafe = async (orderId) => {
    if (!window.confirm('Approve this Paysafe payment? This will mark the order as PAID and send confirmation email.')) {
      return;
    }

    setMarkingPaidId(orderId);
    try {
      const response = await axios.post(
        `${API}/admin/paysafe/${orderId}/approve`,
        { notes: 'Approved via admin dashboard' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Paysafe payment approved. ${response.data.email_sent ? 'Email sent.' : ''}`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve payment');
    } finally {
      setMarkingPaidId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>All Orders ({filteredOrders.length})</span>
            <div className="flex gap-2">
              {selectedOrders.size > 0 && (
                <Button 
                  onClick={handleBulkCancel} 
                  variant="destructive" 
                  size="sm"
                  disabled={bulkCancelling}
                  data-testid="bulk-cancel-btn"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  {bulkCancelling ? 'Cancelling...' : `Cancel ${selectedOrders.size} Selected`}
                </Button>
              )}
              <Button onClick={selectAllCancellable} variant="outline" size="sm">
                {selectedOrders.size > 0 ? <CheckSquare className="h-4 w-4 mr-1" /> : <Square className="h-4 w-4 mr-1" />}
                Select All
              </Button>
              <Button onClick={fetchOrders} variant="outline" size="sm">
                Refresh
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <AdminSearchFilter
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search by order ID, email, or customer name..."
            filters={[
              {
                key: 'status',
                label: 'Status',
                value: statusFilter,
                onChange: setStatusFilter,
                options: [
                  { value: 'all', label: 'All Status' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'processing', label: 'Processing' },
                  { value: 'shipped', label: 'Shipped' },
                  { value: 'delivered', label: 'Delivered' },
                  { value: 'cancelled', label: 'Cancelled' }
                ]
              },
              {
                key: 'payment',
                label: 'Payment',
                value: paymentFilter,
                onChange: setPaymentFilter,
                options: [
                  { value: 'all', label: 'All Payments' },
                  { value: 'paid', label: 'Paid' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'failed', label: 'Failed' }
                ]
              },
              {
                key: 'date',
                label: 'Date',
                value: dateFilter,
                onChange: setDateFilter,
                options: [
                  { value: 'all', label: 'All Time' },
                  { value: 'today', label: 'Today' },
                  { value: 'week', label: 'This Week' },
                  { value: 'month', label: 'This Month' }
                ]
              }
            ]}
            onClearFilters={clearFilters}
            resultCount={filteredOrders.length}
            resultLabel="orders"
          />
          
          <div className="space-y-4">
            {filteredOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No orders found matching your filters
              </div>
            ) : filteredOrders.map((order) => (
              <div key={order.id} className="border rounded-lg p-6" data-testid={`order-${order.id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {canCancelOrder(order) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleOrderSelection(order.id);
                          }}
                          className="flex-shrink-0"
                          data-testid={`select-order-${order.id}`}
                        >
                          {selectedOrders.has(order.id) ? (
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Square className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      )}
                      <Package className="h-5 w-5 text-gray-400" />
                      <span className="font-bold">Order {order.id.substring(0, 8)}...</span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Customer:</strong> {order.user_email}</p>
                      <p><strong>Total:</strong> ${order.total_amount.toFixed(2)}</p>
                      <p><strong>Date:</strong> {new Date(order.created_at).toLocaleString()}</p>
                      {order.tracking_number && (
                        <p><strong>Tracking:</strong> <span className="font-mono bg-gray-100 px-2 py-1 rounded">{order.tracking_number}</span></p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <span className={`px-3 py-1 rounded text-xs font-medium ${
                        order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 
                        order.payment_status === 'pending_approval' ? 'bg-amber-100 text-amber-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.payment_status === 'pending_approval' ? 'Awaiting Approval' : order.payment_status}
                      </span>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                        {order.maksura_tracking?.status?.replace('_', ' ') || 'pending'}
                      </span>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedOrder(order);
                          setOverrideData({ status: '', reason: '' });
                          setShowOverrideModal(true);
                        }}
                        data-testid={`override-status-${order.id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Override Status
                      </Button>

                      {order.payment_status === 'paid' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleGenerateLabel(order)}
                          data-testid={`generate-label-${order.id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Label
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`/tracking/${order.id}`, '_blank')}
                        data-testid={`view-order-${order.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>

                      {/* Mark as Paid button for pending orders */}
                      {order.payment_status !== 'paid' && order.payment_status !== 'cancelled' && (
                        order.payment_method === 'paysafe' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleApprovePaysafe(order.id)}
                            disabled={markingPaidId === order.id}
                            data-testid={`approve-paysafe-${order.id}`}
                          >
                            <DollarSign className="h-4 w-4 mr-1" />
                            {markingPaidId === order.id ? 'Approving...' : 'Approve Payment'}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleMarkPaid(order.id)}
                            disabled={markingPaidId === order.id}
                            data-testid={`mark-paid-${order.id}`}
                          >
                            <DollarSign className="h-4 w-4 mr-1" />
                            {markingPaidId === order.id ? 'Processing...' : 'Mark Paid'}
                          </Button>
                        )
                      )}

                      {/* Resend confirmation email for paid orders */}
                      {order.payment_status === 'paid' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResendEmail(order.id)}
                          disabled={resendingEmailId === order.id}
                          data-testid={`resend-email-${order.id}`}
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          {resendingEmailId === order.id ? 'Sending...' : 'Resend Email'}
                        </Button>
                      )}

                      {canCancelOrder(order) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleSingleCancel(order.id)}
                          disabled={cancellingOrderId === order.id}
                          data-testid={`cancel-order-${order.id}`}
                        >
                          <Ban className="h-4 w-4 mr-1" />
                          {cancellingOrderId === order.id ? 'Cancelling...' : 'Cancel'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Status Override Modal */}
      <Dialog open={showOverrideModal} onOpenChange={setShowOverrideModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override Order Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>New Status</Label>
              <Select
                value={overrideData.status}
                onValueChange={(value) => setOverrideData({ ...overrideData, status: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Reason</Label>
              <Textarea
                value={overrideData.reason}
                onChange={(e) => setOverrideData({ ...overrideData, reason: e.target.value })}
                placeholder="Why are you changing this status?"
                className="mt-1"
              />
            </div>

            {/* Email notification info */}
            <div className="bg-blue-50 border border-blue-200 p-3 rounded text-sm">
              <p className="font-medium text-blue-800 flex items-center gap-1">
                <Mail className="h-4 w-4" />
                Email Notification
              </p>
              <p className="text-blue-700 text-xs mt-1">
                {overrideData.status === 'shipped' && 'Customer will receive shipping notification with tracking number.'}
                {overrideData.status === 'delivered' && 'Customer will receive delivery confirmation email.'}
                {overrideData.status === 'processing' && 'Customer will receive order processing confirmation.'}
                {overrideData.status === 'in_transit' && 'Status will be updated. Customer can track via dashboard.'}
                {!overrideData.status && 'Select a status to see email notification details.'}
              </p>
            </div>

            <Button onClick={handleStatusOverride} className="w-full">
              Update Status
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shipping Label Modal */}
      <Dialog open={showLabelModal} onOpenChange={setShowLabelModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Shipping Label</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div 
              className="border p-4 max-h-96 overflow-auto" 
              dangerouslySetInnerHTML={{ __html: shippingLabel }}
            />
            <div className="flex gap-2 mt-4">
              <Button onClick={printLabel} className="flex-1">
                Print Label
              </Button>
              <Button variant="outline" onClick={() => setShowLabelModal(false)} className="flex-1">
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersManagement;
